import React from 'react';
import { useTranslation } from 'react-i18next';
import { ZmanimData } from '../../types';
import { ZMAN_DISPLAY_FIELDS, getUpcomingZmanField } from '../../utils/zmanimDisplay';
import { getCurrentTimeHHMM, applyOffset } from '../../utils/timeUtils';

interface ZmanimTodayProps {
  todayZmanim: ZmanimData | null;
  tomorrowZmanim: ZmanimData | null;
  loading: boolean;
}

export default function ZmanimToday({ todayZmanim, tomorrowZmanim, loading }: ZmanimTodayProps) {
  const { t } = useTranslation();

  if (!loading && !todayZmanim) return null;

  const now = getCurrentTimeHHMM();
  // Today's last zman (Tzeit Rabbeinu Tam) + 10-minute grace has passed — roll over to tomorrow.
  const lastZman = todayZmanim ? todayZmanim.TzetRabbeinuTam ?? todayZmanim.Tzet : '';
  const todayDone = todayZmanim ? applyOffset(lastZman, 10) < now : false;
  const activeZmanim = todayDone ? tomorrowZmanim : todayZmanim;
  const upcomingField = activeZmanim ? getUpcomingZmanField(activeZmanim) : null;

  return (
    <div className="bg-paper border border-ink/10 rounded-[16px] p-4 space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-ink">{t('home.zmanimHeading')}</h2>
        {todayDone && (
          <span className="text-xs text-ash uppercase tracking-wide">· {t('home.tomorrow')}</span>
        )}
      </div>

      {loading || !activeZmanim ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ZMAN_DISPLAY_FIELDS.map(({ field }) => (
            <div key={field} className="h-10 rounded-[10px] bg-fog animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {ZMAN_DISPLAY_FIELDS.map(({ field, i18nKey }) => {
            const isUpcoming = field === upcomingField;
            return (
              <div key={field} className={isUpcoming ? 'bg-fog rounded-[10px] p-2 -m-2' : undefined}>
                <p className="text-xs text-ash">
                  {isUpcoming && (
                    <>
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 me-1" />
                      {t('zmanim.upcoming')}{' '}
                    </>
                  )}
                  {t(`zmanim.${i18nKey}`)}
                </p>
                <p className="text-sm font-semibold text-ink tabular-nums">
                  {activeZmanim[field] as string}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
